import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { georef, type GeorefCrsOption, type GeorefGcp, type GeorefMap } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { MapLibreMap } from '@/components/MapLibreMap';
import { GeorefCanvas, type Crop, type Tryb } from '@/components/georef/GeorefCanvas';

/**
 * Edytor dopasowania mapy — ekran, na którym człowiek ZATWIERDZA to, co policzył automat.
 *
 * Trzy rzeczy muszą tu być widoczne bez klikania: co program wyciął jako pole mapy, skąd wziął
 * punkty kontrolne (i który z nich się nie zgadza) oraz jak raster kładzie się na realnym podkładzie.
 * Dopiero mając to przed oczami, zatwierdzenie jest decyzją, a nie klikaniem w ciemno.
 */
export function GeorefEditorPage() {
  const { id } = useParams();
  const mapId = Number(id);
  const nav = useNavigate();

  const [mapa, setMapa] = useState<GeorefMap | null>(null);
  const [uklady, setUklady] = useState<GeorefCrsOption[]>([]);
  const [tryb, setTryb] = useState<Tryb>('crop');
  const [crop, setCrop] = useState<Crop | null>(null);
  const [gcps, setGcps] = useState<GeorefGcp[]>([]);
  const [wybrany, setWybrany] = useState<number | null>(null);
  const [obrys, setObrys] = useState<number[][]>([]);
  const [zoom, setZoom] = useState(1);
  const [krycie, setKrycie] = useState(70);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [rasterNonce, setRasterNonce] = useState(0);

  const wczytaj = (m: GeorefMap) => {
    setMapa(m);
    setCrop(m.cropX != null && m.cropY != null && m.cropW != null && m.cropH != null
      ? { x: m.cropX, y: m.cropY, w: m.cropW, h: m.cropH } : null);
    setGcps(m.gcps);
    setWybrany(null);
  };

  useEffect(() => {
    if (!Number.isFinite(mapId)) return;
    georef.get(mapId).then(wczytaj).catch(e => setErr(e?.message ?? String(e)));
    georef.crsOptions().then(setUklady).catch(() => setUklady([]));
  }, [mapId]);

  const akcja = <T,>(nazwa: string, p: Promise<T>, po?: (r: T) => void) => {
    setBusy(nazwa); setErr(null); setInfo(null);
    p.then(r => { po?.(r); }).catch(e => setErr(e?.message ?? String(e))).finally(() => setBusy(null));
  };

  const cropZmienione = !!mapa && !!crop && (crop.x !== mapa.cropX || crop.y !== mapa.cropY
    || crop.w !== mapa.cropW || crop.h !== mapa.cropH);
  const punktyZmienione = useMemo(() => {
    if (!mapa) return false;
    if (gcps.length !== mapa.gcps.length) return true;
    return gcps.some((g, i) => {
      const o = mapa.gcps[i];
      return !o || g.px !== o.px || g.py !== o.py || g.active !== o.active
        || g.easting !== o.easting || g.northing !== o.northing || g.lat !== o.lat || g.lon !== o.lon;
    });
  }, [gcps, mapa]);

  if (!mapa) {
    return <div className="text-ink-400 text-[13px]">{err ? <span className="text-red-600">{err}</span> : 'Ładowanie…'}</div>;
  }

  const aktywne = gcps.filter(g => g.active).length;
  const statusPill = mapa.status === 'APPROVED' ? 'pill pill-green'
    : mapa.status === 'PROPOSED' ? 'pill pill-amber'
      : mapa.status === 'REJECTED' ? 'pill pill-red' : 'pill pill-gray';
  const zlyKat = mapa.skosDeg != null && Math.abs(mapa.skosDeg) > 1.5;
  const zlaAnizo = mapa.anizotropia != null && (mapa.anizotropia < 0.97 || mapa.anizotropia > 1.03);

  const zapiszPunkty = (lista: GeorefGcp[]) => akcja('gcps', georef.saveGcps(mapId, lista.map(g => ({
    id: g.id, px: g.px, py: g.py, groundKind: g.groundKind,
    easting: g.easting, northing: g.northing, lat: g.lat, lon: g.lon,
    label: g.label, source: g.source, active: g.active,
  }))), wczytaj);

  const ustawPole = (i: number, patch: Partial<GeorefGcp>) =>
    setGcps(l => l.map((g, idx) => idx === i ? { ...g, ...patch } : g));

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <button className="btn btn-ghost btn-xs mb-1" onClick={() => nav('/dopasowanie-map')}>
            <Icon name="arrow" className="w-3 h-3 rotate-180" /> Wszystkie mapy
          </button>
          <h1 className="truncate">{mapa.name}</h1>
          <div className="text-ink-400 text-[12px] font-mono truncate">
            {mapa.sourceFilename}
            {mapa.pageCount && mapa.pageCount > 1 ? ` · strona ${mapa.pageNumber}/${mapa.pageCount}` : ''}
            {mapa.rasterWidth ? ` · ${mapa.rasterWidth}×${mapa.rasterHeight} px` : ''}
            {mapa.renderDpi ? ` · ${mapa.renderDpi} dpi` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={statusPill}>{mapa.statusLabel}</span>
          {mapa.status !== 'APPROVED' && (
            <button
              className="btn btn-primary btn-sm"
              disabled={!!busy || mapa.rmsM == null || cropZmienione || punktyZmienione}
              title={mapa.rmsM == null ? 'Najpierw musi powstać transformacja (≥3 punkty kontrolne)'
                : cropZmienione || punktyZmienione ? 'Zapisz najpierw zmiany' : 'Zatwierdź dopasowanie i utwórz warstwę'}
              onClick={() => akcja('approve', georef.approve(mapId), m => { wczytaj(m); setInfo('Zatwierdzone — mapa jest już warstwą w katalogu map.'); })}
            ><Icon name="check" className="w-3.5 h-3.5" /> Zatwierdź</button>
          )}
          {mapa.status === 'APPROVED' && (
            <span className="text-[11px] text-emerald-700">
              ✓ {mapa.approvedBy ?? ''} · warstwa <span className="font-mono">{mapa.layerCode}</span>
            </span>
          )}
          <button className="btn btn-secondary btn-sm" disabled={!!busy}
            onClick={() => akcja('reject', georef.reject(mapId), wczytaj)}>Odrzuć</button>
          <button className="btn btn-ghost btn-sm text-red-600" disabled={!!busy}
            onClick={() => { if (confirm('Usunąć tę mapę wraz z punktami kontrolnymi?')) akcja('del', georef.remove(mapId), () => nav('/dopasowanie-map')); }}>
            <Icon name="x" className="w-3.5 h-3.5" /> Usuń
          </button>
        </div>
      </header>

      {mapa.statusNote && (
        <div className={'mb-3 text-[12px] rounded px-3 py-2 border '
          + (mapa.rmsM == null ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-ink-50 border-ink-200 text-ink-700')}>
          <span className="font-semibold">Automat: </span>{mapa.statusNote}
        </div>
      )}
      {err && <div className="mb-3 text-[12px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
      {info && <div className="mb-3 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{info}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
        {/* ── obraz mapy z warstwą interakcji ── */}
        <div className="card flex flex-col min-h-0">
          <div className="card-head !py-2 gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              {([['crop', 'Wycięcie'], ['gcp', `Punkty (${aktywne}/${gcps.length})`], ['trace', 'Obrys']] as const).map(([t, label]) => (
                <button key={t} onClick={() => setTryb(t)}
                  className={'btn btn-sm ' + (tryb === t ? 'btn-primary' : 'btn-ghost')}>{label}</button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-ink-400">Powiększenie</span>
              <input type="range" min={1} max={5} step={0.25} value={zoom}
                onChange={e => setZoom(Number(e.target.value))} className="w-28 accent-water-500" />
              <span className="font-mono text-[11px] text-ink-500 w-8">{zoom.toFixed(1)}×</span>
            </div>
          </div>
          <div className="card-body !p-2">
            {tryb === 'crop' && (
              <div className="text-[11px] text-ink-500 mb-2">
                Przeciągnij narożniki, aby poprawić granice pola mapy. Ciemny obszar (opis, legenda,
                marginesy) nie trafi na warstwę.
                {mapa.cropSourceLabel && <> Źródło: <b>{mapa.cropSourceLabel}</b>
                  {mapa.cropConfidence != null && ` (pewność ${Math.round(mapa.cropConfidence * 100)}%)`}.</>}
              </div>
            )}
            {tryb === 'gcp' && (
              <div className="text-[11px] text-ink-500 mb-2">
                Przeciągnij punkt, żeby poprawić jego położenie; kliknięcie w wolne miejsce dodaje nowy
                punkt (współrzędne uzupełnij po prawej). Czerwone = duże residuum.
              </div>
            )}
            {tryb === 'trace' && (
              <div className="text-[11px] text-ink-500 mb-2">
                Klikaj po obwodzie obszaru — obrys zostanie przeliczony na współrzędne WGS84.
              </div>
            )}
            <GeorefCanvas
              rasterUrl={georef.rasterUrl(mapId) + (rasterNonce ? `?v=${rasterNonce}` : '')}
              width={mapa.rasterWidth ?? 1000}
              height={mapa.rasterHeight ?? 1000}
              tryb={tryb}
              crop={crop}
              onCrop={setCrop}
              gcps={gcps}
              onMoveGcp={(i, px, py) => ustawPole(i, { px, py })}
              onAddGcp={(px, py) => {
                setGcps(l => [...l, {
                  id: null, px, py, groundKind: 'GRID', easting: null, northing: null,
                  lat: null, lon: null, label: null, source: 'MANUAL', active: true, residualM: null,
                }]);
                setWybrany(gcps.length);
              }}
              wybranyGcp={wybrany}
              onWybierzGcp={setWybrany}
              obrys={obrys}
              onObrys={setObrys}
              zoom={zoom}
            />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {tryb === 'crop' && (
                <>
                  <button className="btn btn-primary btn-sm" disabled={!cropZmienione || !!busy}
                    onClick={() => crop && akcja('crop', georef.setCrop(mapId, crop), wczytaj)}>
                    <Icon name="check" className="w-3.5 h-3.5" /> Zapisz wycięcie
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={!!busy}
                    onClick={() => akcja('auto', georef.analyze(mapId, true, false), m => { wczytaj(m); setInfo('Automat przeliczony.'); })}>
                    <Icon name="sparkles" className="w-3.5 h-3.5" /> Wykryj automatycznie
                  </button>
                  {mapa.pageCount && mapa.pageCount > 1 && (
                    <label className="flex items-center gap-1 text-[12px] text-ink-600 ml-2">
                      Strona
                      <input type="number" min={1} max={mapa.pageCount} defaultValue={mapa.pageNumber}
                        className="input !w-16 !py-1"
                        onBlur={e => {
                          const p = Number(e.target.value);
                          if (p && p !== mapa.pageNumber) {
                            akcja('page', georef.setPage(mapId, p), m => { wczytaj(m); setRasterNonce(n => n + 1); });
                          }
                        }} />
                      z {mapa.pageCount}
                    </label>
                  )}
                </>
              )}
              {tryb === 'gcp' && (
                <>
                  <button className="btn btn-primary btn-sm" disabled={!punktyZmienione || !!busy}
                    onClick={() => zapiszPunkty(gcps)}>
                    <Icon name="check" className="w-3.5 h-3.5" /> Zapisz punkty i przelicz
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={!!busy}
                    onClick={() => akcja('grid', georef.analyze(mapId, true, false), m => { wczytaj(m); setInfo('Odczytano siatkę ponownie.'); })}>
                    <Icon name="sparkles" className="w-3.5 h-3.5" /> Odczytaj siatkę
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={!!busy}
                    title="Model rozpozna na mapie studnie ujęcia o znanych współrzędnych (szkic do poprawy)"
                    onClick={() => akcja('ai', georef.analyze(mapId, false, true), m => { wczytaj(m); setInfo('AI wskazało obiekty — sprawdź pozycje.'); })}>
                    <Icon name="pen-ai" className="w-3.5 h-3.5" /> Wskaż studnie (AI)
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={!!busy || mapa.rmsM == null}
                    title={mapa.rmsM == null
                      ? 'Najpierw musi istnieć przybliżona transformacja — dopasowanie do podkładu tylko ją poprawia'
                      : 'Porównuje mapę z podkładem topograficznym i koryguje przesunięcie'}
                    onClick={() => akcja('basemap', georef.matchBasemap(mapId), m => { wczytaj(m); setInfo('Dopasowanie do podkładu policzone — przeczytaj wynik nad podglądem.'); })}>
                    <Icon name="layers" className="w-3.5 h-3.5" /> Dopasuj do podkładu
                  </button>
                </>
              )}
              {tryb === 'trace' && (
                <>
                  <button className="btn btn-secondary btn-sm" disabled={!obrys.length}
                    onClick={() => setObrys(o => o.slice(0, -1))}>Cofnij wierzchołek</button>
                  <button className="btn btn-secondary btn-sm" disabled={!obrys.length}
                    onClick={() => setObrys([])}>Wyczyść</button>
                  <button className="btn btn-primary btn-sm" disabled={obrys.length < 3 || !!busy || mapa.rmsM == null}
                    onClick={() => akcja('trace', georef.traceToGeoJson(mapId, obrys), r => {
                      navigator.clipboard?.writeText(r.geoJson).catch(() => {});
                      const blob = new Blob([r.geoJson], { type: 'application/geo+json' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `obrys-mapa-${mapId}.geojson`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                      setInfo('Obrys przeliczony na WGS84 — skopiowany do schowka i pobrany jako .geojson.');
                    })}>
                    <Icon name="download" className="w-3.5 h-3.5" /> Przelicz obrys na WGS84
                  </button>
                </>
              )}
              {busy && <span className="text-[11px] text-water-600">pracuję…</span>}
            </div>
          </div>
        </div>

        {/* ── panel jakości i punktów ── */}
        <aside className="flex flex-col gap-4 min-h-0">
          <div className="card">
            <div className="card-head !py-2"><div className="card-title">Jakość dopasowania</div></div>
            <div className="card-body !p-3 flex flex-col gap-2 text-[12px]">
              <label className="field">
                <span className="field-label">Układ współrzędnych</span>
                <SearchableSelect value={mapa.mapCrs ?? ''} onChange={v => v && akcja('crs', georef.setCrs(mapId, v), wczytaj)}>
                  <option value="">— nierozpoznany —</option>
                  {uklady.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
                </SearchableSelect>
                <span className="field-hint">
                  {mapa.crsSource === 'OCR_GRID' ? 'Rozpoznany z opisów siatki.'
                    : mapa.crsSource === 'GRID_VALUES' ? 'Wynika z wartości punktów siatki.'
                      : mapa.crsSource === 'MANUAL' ? 'Ustawiony ręcznie.'
                        : mapa.crsSource === 'DEFAULT' ? 'Przyjęty domyślnie (punkty mają lat/lon).' : ''}
                </span>
              </label>
              <Wiersz etykieta="Błąd dopasowania (RMS)">
                {mapa.rmsM == null ? <span className="text-ink-400">brak transformacji</span>
                  : !mapa.rmsMiarodajny
                    ? <span className="text-amber-600">{mapa.rmsM.toFixed(1)} m — niemiarodajny (3 punkty)</span>
                    : <span className={mapa.rmsM > 25 ? 'text-amber-600' : 'text-emerald-600'}>{mapa.rmsM.toFixed(1)} m</span>}
              </Wiersz>
              <Wiersz etykieta="Skala rastra">
                {mapa.skalaMPx ? `${mapa.skalaMPx.toFixed(2)} m/piksel` : '—'}
              </Wiersz>
              <Wiersz etykieta="Obrót">{mapa.obrotDeg != null ? `${mapa.obrotDeg.toFixed(2)}°` : '—'}</Wiersz>
              <Wiersz etykieta="Skos">
                <span className={zlyKat ? 'text-amber-600' : ''}>{mapa.skosDeg != null ? `${mapa.skosDeg.toFixed(2)}°` : '—'}</span>
              </Wiersz>
              <Wiersz etykieta="Anizotropia">
                <span className={zlaAnizo ? 'text-amber-600' : ''}>{mapa.anizotropia != null ? mapa.anizotropia.toFixed(3) : '—'}</span>
              </Wiersz>
              {(zlyKat || zlaAnizo) && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Transformacja deformuje mapę — zwykle winny jest jeden punkt kontrolny. Sprawdź ten
                  z największym residuum.
                </div>
              )}
            </div>
          </div>

          <div className="card flex flex-col min-h-0">
            <div className="card-head !py-2">
              <div className="card-title">Punkty kontrolne</div>
              <span className="pill pill-water ml-2">{aktywne}/{gcps.length}</span>
            </div>
            <div className="card-body !p-1.5 overflow-auto max-h-[40vh]">
              {gcps.length === 0 && <div className="text-center text-ink-400 text-[12px] py-6">
                Brak punktów. Odczytaj siatkę, poproś AI o wskazanie studni albo dodaj ręcznie na obrazie.
              </div>}
              {gcps.map((g, i) => (
                <div key={g.id ?? `n${i}`}
                  onClick={() => { setWybrany(i); setTryb('gcp'); }}
                  className={'px-2 py-1.5 rounded border mb-1 cursor-pointer '
                    + (wybrany === i ? 'border-water-500 bg-water-50/60' : 'border-transparent hover:bg-ink-50')
                    + (g.active ? '' : ' opacity-50')}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={g.active} className="w-3 h-3 accent-water-500"
                      onClick={e => e.stopPropagation()}
                      onChange={e => ustawPole(i, { active: e.target.checked })} />
                    <span className="flex-1 truncate text-[12px] text-ink-800">{g.label ?? `Punkt ${i + 1}`}</span>
                    {g.residualM != null && (
                      <span className={'font-mono text-[10px] ' + (g.residualM > 25 ? 'text-red-600' : 'text-ink-400')}>
                        {g.residualM.toFixed(1)} m
                      </span>
                    )}
                    <button className="text-ink-300 hover:text-red-500" title="Usuń punkt"
                      onClick={e => { e.stopPropagation(); setGcps(l => l.filter((_, idx) => idx !== i)); setWybrany(null); }}>
                      <Icon name="x" className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-[10px] text-ink-400 font-mono truncate">
                    {g.sourceLabel ?? g.source} · piksel {Math.round(g.px)}, {Math.round(g.py)}
                  </div>
                  {wybrany === i && (
                    <div className="mt-1.5 grid gap-1.5" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {(['GRID', 'WGS84'] as const).map(k => (
                          <button key={k}
                            className={'btn btn-xs flex-1 justify-center ' + (g.groundKind === k ? 'btn-primary' : 'btn-secondary')}
                            onClick={() => ustawPole(i, { groundKind: k })}>
                            {k === 'GRID' ? 'Siatka (X/Y)' : 'WGS84 (lat/lon)'}
                          </button>
                        ))}
                      </div>
                      {g.groundKind === 'GRID' ? (
                        <div className="flex gap-1">
                          <input className="input !py-1 !text-[11px]" placeholder="X (northing)" value={g.northing ?? ''}
                            onChange={e => ustawPole(i, { northing: e.target.value === '' ? null : Number(e.target.value) })} />
                          <input className="input !py-1 !text-[11px]" placeholder="Y (easting)" value={g.easting ?? ''}
                            onChange={e => ustawPole(i, { easting: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <input className="input !py-1 !text-[11px]" placeholder="szer. (lat)" value={g.lat ?? ''}
                            onChange={e => ustawPole(i, { lat: e.target.value === '' ? null : Number(e.target.value) })} />
                          <input className="input !py-1 !text-[11px]" placeholder="dług. (lon)" value={g.lon ?? ''}
                            onChange={e => ustawPole(i, { lon: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                      )}
                      <input className="input !py-1 !text-[11px]" placeholder="opis punktu" value={g.label ?? ''}
                        onChange={e => ustawPole(i, { label: e.target.value })} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {punktyZmienione && (
              <div className="px-3 py-2 border-t border-ink-100">
                <button className="btn btn-primary btn-sm w-full justify-center" disabled={!!busy}
                  onClick={() => zapiszPunkty(gcps)}>Zapisz punkty i przelicz</button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* ── weryfikacja na realnym podkładzie ── */}
      <div className="card mt-4">
        <div className="card-head !py-2">
          <div className="card-title">Podgląd na mapie</div>
          <span className="text-[11px] text-ink-400">
            {mapa.rmsM == null ? 'Dostępny po policzeniu transformacji.'
              : 'Sprawdź, czy raster kładzie się na podkładzie tam, gdzie powinien.'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-ink-400">Krycie</span>
            <input type="range" min={0} max={100} value={krycie} onChange={e => setKrycie(Number(e.target.value))}
              className="w-32 accent-water-500" />
            <span className="font-mono text-[11px] text-ink-500 w-9">{krycie}%</span>
          </div>
        </div>
        <div className="card-body !p-0 h-[460px]">
          {mapa.rmsM != null && mapa.bboxWgs84 ? (
            <MapLibreMap
              height="100%"
              previewRaster={{ url: georef.previewTileUrl(mapId), opacity: krycie }}
              focusGeometry={{ geoJson: bboxJakoPolygon(mapa.bboxWgs84), nonce: mapa.gcps.length + Math.round(mapa.rmsM ?? 0) }}
            />
          ) : (
            <div className="h-full grid place-items-center text-ink-400 text-[13px]">
              Najpierw musi powstać transformacja (co najmniej 3 punkty kontrolne).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Zasięg mapy jako poligon — MapLibreMap robi do niego dolot (focusGeometry). */
function bboxJakoPolygon(b: [number, number, number, number]): string {
  const [w, s, e, n] = b;
  return JSON.stringify({
    type: 'Polygon',
    coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
  });
}

function Wiersz({ etykieta, children }: { etykieta: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-ink-500 flex-1">{etykieta}</span>
      <span className="font-mono tabular-nums">{children}</span>
    </div>
  );
}
